// ANGEL FLIGHT (color) — ColorWorld: the sun-lit, textured, scrolling battlefield
// + the COLOR horde. The COLOR sibling of WorldView3D.
//
// Implements WorldRenderer for the GameLoop and keeps WorldView3D's contracts
// IDENTICAL — it registers itself as the view's raycast hit provider, owns a
// pooled GlbEnemyView per live enemy keyed by id (onSpawned/onKilled/retire),
// syncs each from ctx.enemies every frame via worldToThree, writes the screen
// cache (e.sx/e.sy/e.scale) back via ctx.view.worldToScreen, and exposes
// raycast(ndcX,ndcY,camera) → EnemyHit. ONLY the ground + lighting + props + the
// enemy material change to color:
//   - a large asphalt PBR ground plane that SCROLLS toward the near edge,
//   - a warm dusk directional "sun" + a cool hemispheric fill (+ optional HDRI),
//   - distance Fog (near/far) and a dusk background,
//   - a recycled low-poly prop pool (wrecks / rubble / barrels) sliding past,
//   - COLOR capsule enemies (GlbEnemyView) instead of heat silhouettes.
//
// All tunables come from config/render.ts (RENDER.*). No per-frame allocations
// in update(); all geometries/materials/textures disposed in destroy().

import * as THREE from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { RENDER } from '../config/render';
import { getDifficulty } from '../config/difficulty';
import { getEnemy } from '../config/enemies';
import { ObjectPool } from '../core/Pool';
import { worldToThree } from './coords';
import { CityBackdrop } from './CityBackdrop';
import { GlbEnemyView } from './GlbEnemyView';
import type { GameContext } from '../systems/System';
import type { WorldRenderer } from '../app/GameLoop';
import type { EnemyHit } from './GameView';

/** A single recycled prop sliding toward the near edge. `worldZ` is its sim-depth
 *  (metres); position is derived from worldToThree({x, y:0, z:worldZ}). */
interface Prop {
  mesh: THREE.Object3D;
  worldX: number;
  worldZ: number;
  active: boolean;
}

export class ColorWorld implements WorldRenderer {
  private ctx!: GameContext;
  private readonly views = new Map<number, GlbEnemyView>();
  private readonly pool: GlbEnemyView[] = [];
  private group!: THREE.Group;
  private ground!: THREE.Mesh;
  private readonly raycaster = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();

  // Ground material + its three scrolling maps (kept for offset advance/dispose).
  private groundMat!: THREE.MeshStandardMaterial;
  private readonly groundTextures: THREE.Texture[] = [];
  private scrollV = 0; // accumulated texture V offset (wraps via RepeatWrapping)

  // Lights (disposed/removed in destroy()).
  private sun!: THREE.DirectionalLight;
  private hemi!: THREE.HemisphereLight;

  // HDRI env (best-effort; null if it fails to load or isn't reached).
  private envMap: THREE.Texture | null = null;
  private pmrem: THREE.PMREMGenerator | null = null;
  private disposed = false;

  // Dusk-gradient sky background (CanvasTexture; disposed in destroy()).
  private skyTexture: THREE.Texture | null = null;

  // Prop pool.
  private propGroup!: THREE.Group;
  private propPool!: ObjectPool<Prop>;
  private readonly liveProps: Prop[] = [];
  private readonly propGeometries: THREE.BufferGeometry[] = [];
  private readonly propMaterials: THREE.Material[] = [];
  // Shared prop PBR maps (loaded ONCE like the ground; disposed in destroy()).
  private readonly propTextures: THREE.Texture[] = [];
  private propVariants: THREE.Object3D[] = []; // template meshes (cloned per prop)

  // Distant burning-city skyline + drifting smoke columns (the war-torn horizon).
  private backdrop!: CityBackdrop;

  // Spawn-haze curtains: translucent fog quads straddling the spawn band so the
  // horde walks OUT of a visible fog bank (disposed in destroy()).
  private readonly hazeMeshes: THREE.Mesh[] = [];
  private hazeGeo: THREE.BufferGeometry | null = null;
  private readonly hazeMaterials: THREE.Material[] = [];
  private hazeTexture: THREE.Texture | null = null;

  // The live reveal-clear plane (m) — the difficulty's revealZM, pushed onto
  // every spawned rig so the fade completes deep in the fog bank.
  private revealClearZM: number = RENDER.spawnHaze.fadeClearZ;
  // Fog-burst puffs at the emergence line (pooled sprites, one per arrival).
  private readonly fogBursts: Array<{ sprite: THREE.Sprite; ageMs: number; active: boolean }> = [];
  private fogBurstIdx = 0;
  private fogBurstTexture: THREE.Texture | null = null;
  // Enemy ids whose emergence puff already fired (cleared on retire/kill).
  private readonly emerged = new Set<number>();

  // Overpass jersey barriers (themes with overpassRails — M1 IS "The Overpass").
  // One InstancedMesh per build, fixed count, scrolled by a modular Z offset.
  private rails: THREE.InstancedMesh | null = null;
  private railGeo: THREE.BufferGeometry | null = null;
  private railMat: THREE.Material | null = null;
  private railOffset = 0;
  private railsPerSide = 0;
  private readonly railMatrix = new THREE.Matrix4();

  init(ctx: GameContext): void {
    this.ctx = ctx;
    const scene = this.scene();
    this.group = new THREE.Group();
    scene.add(this.group);

    // Register as the view's enemy hit-test provider (raycast firing).
    const view = ctx.view as unknown as { setHitProvider?: (w: ColorWorld) => void };
    view.setHitProvider?.(this);

    this.buildEnvironment(scene);
    this.buildGround(scene);
    this.buildLights(scene);
    this.buildProps(scene);
    this.backdrop = new CityBackdrop(scene, this.theme().skylineFireGlow);
    if (this.theme().overpassRails) this.buildRails();
    this.buildSpawnHaze();
    this.buildFogBursts();
    // Reveal plane per difficulty (recruit clears the murk deep; ace holds the
    // ghost close) — wired onto each rig at spawn.
    this.revealClearZM = getDifficulty(ctx.difficulty).revealZM;
    this.loadHdri(scene);

    ctx.bus.on('enemy:spawned', this.onSpawned);
    ctx.bus.on('enemy:killed', this.onKilled);
    ctx.bus.on('hit:landed', this.onHit);
  }

  /** Segmented concrete jersey barriers along both road edges, scrolling with
   *  the world — the cheapest honest read of "this road is an ELEVATED
   *  overpass" (M1's name). Fixed instance count; update() slides them via a
   *  modular offset, so no spawn/despawn and no per-frame allocation. */
  private buildRails(): void {
    const O = RENDER.overpass;
    const C = RENDER.props.concrete;
    const period = O.segmentLenM + O.segmentGapM;
    const span = O.spanFarZ - O.spanNearZ;
    this.railsPerSide = Math.ceil(span / period);
    this.railGeo = new THREE.BoxGeometry(O.railWidthM, O.railHeightM, O.segmentLenM);
    this.railMat = new THREE.MeshStandardMaterial({
      color: C.tint,
      roughness: C.roughness,
      metalness: C.metalness,
    });
    this.rails = new THREE.InstancedMesh(this.railGeo, this.railMat, this.railsPerSide * 2);
    this.rails.frustumCulled = false;
    this.group.add(this.rails);
    this.layoutRails();
  }

  /** Recompute every barrier matrix from the current scroll offset. */
  private layoutRails(): void {
    if (!this.rails) return;
    const O = RENDER.overpass;
    const period = O.segmentLenM + O.segmentGapM;
    const span = this.railsPerSide * period;
    for (let side = 0; side < 2; side++) {
      const x = side === 0 ? -O.railXM : O.railXM;
      for (let i = 0; i < this.railsPerSide; i++) {
        // Sim-depth slides toward the near edge; wrap within the span band.
        let z = O.spanNearZ + (((i * period - this.railOffset) % span) + span) % span;
        this.railMatrix.makeTranslation(x, O.railHeightM / 2, -z);
        this.rails.setMatrixAt(side * this.railsPerSide + i, this.railMatrix);
      }
    }
    this.rails.instanceMatrix.needsUpdate = true;
  }

  /** Directional flinch on the struck soldier (stagger him along the shot dir). */
  private readonly onHit = (p: { targetId: number; dirX?: number }): void => {
    if (p.dirX !== undefined) this.views.get(p.targetId)?.flinch(p.dirX);
  };

  private scene(): THREE.Scene {
    // The view is a GameView3D in-game; cast through the seam to reach the scene.
    const v = this.ctx.view as unknown as { scene: THREE.Scene };
    return v.scene;
  }

  /** Fog + a vertical dusk-gradient background so the world recedes into warm
   *  atmosphere and the spawn/recycle line hides. The gradient (deep indigo
   *  zenith → warm amber horizon) reads as a real golden-hour sky rather than a
   *  flat fog-coloured wall. */
  /** The active mission's biome theme (mood overrides). Falls back to dusk. */
  private theme(): (typeof RENDER.themes)[keyof typeof RENDER.themes] {
    const key = this.ctx.mission.theme as keyof typeof RENDER.themes;
    return RENDER.themes[key] ?? RENDER.themes['dusk-suburb'];
  }

  private buildEnvironment(scene: THREE.Scene): void {
    const t = this.theme();
    scene.fog = new THREE.Fog(t.fogColor, t.fogNear, t.fogFar);
    scene.background = this.buildSkyTexture();
  }

  /** A small CanvasTexture painting the 3-stop sky gradient for the active theme. */
  private buildSkyTexture(): THREE.Texture {
    const t = this.theme();
    const s = { top: t.skyTop, mid: t.skyMid, bottom: t.skyBottom };
    const c = document.createElement('canvas');
    c.width = 4;
    c.height = 256;
    const g2d = c.getContext('2d')!;
    // The background texture is SCREEN-stretched and the ground owns the lower
    // ~77% of the frame, so only the gradient's top ~quarter is ever visible.
    // Compress the zenith→horizon ramp into that band (amber horizon by 0.26,
    // held to the bottom) — with the old 0/0.62/1.0 stops the visible sky was
    // all zenith-indigo and the warm horizon glow was hidden behind the ground.
    const grad = g2d.createLinearGradient(0, 0, 0, 256);
    grad.addColorStop(0, '#' + s.top.toString(16).padStart(6, '0'));
    grad.addColorStop(0.13, '#' + s.mid.toString(16).padStart(6, '0'));
    grad.addColorStop(0.26, '#' + s.bottom.toString(16).padStart(6, '0'));
    grad.addColorStop(1, '#' + s.bottom.toString(16).padStart(6, '0'));
    g2d.fillStyle = grad;
    g2d.fillRect(0, 0, 4, 256);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    this.skyTexture = tex;
    return tex;
  }

  /** The large textured asphalt ground plane (scrolls in update()). */
  private buildGround(scene: THREE.Scene): void {
    const w = RENDER.world;
    const loader = new THREE.TextureLoader();
    const base = import.meta.env.BASE_URL + 'assets/textures/';

    const colorMap = loader.load(base + 'asphalt_color.jpg');
    colorMap.colorSpace = THREE.SRGBColorSpace;
    const normalMap = loader.load(base + 'asphalt_normal.jpg');
    const roughMap = loader.load(base + 'asphalt_roughness.jpg');

    for (const tex of [colorMap, normalMap, roughMap]) {
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(w.groundTextureRepeat, w.groundTextureRepeat);
      tex.anisotropy = 4;
      this.groundTextures.push(tex);
    }

    this.groundMat = new THREE.MeshStandardMaterial({
      map: colorMap,
      normalMap,
      roughnessMap: roughMap,
      // Warm earthy tint multiplied over the cold gray asphalt so the sun rake
      // reads amber, not blue-gray. Slightly < 1 roughness lets the low sun catch
      // a faint sheen on the asphalt (wet-dusk-street feel) for contrast.
      color: new THREE.Color(this.theme().groundTint),
      roughness: RENDER.ground.roughness,
      metalness: RENDER.ground.metalness,
      normalScale: new THREE.Vector2(RENDER.ground.normalScale, RENDER.ground.normalScale),
      // Dial the HDRI WAY down per-material (r160 has no scene.environmentIntensity):
      // at full strength the dusk HDRI floods the asphalt flat-gray and kills the
      // warm-sun contrast. Faint IBL whisper only; the lights carry the mood.
      envMapIntensity: RENDER.light.envIntensity,
      fog: true,
    });

    const groundGeo = new THREE.PlaneGeometry(w.groundSizeM, w.groundSizeM);
    this.ground = new THREE.Mesh(groundGeo, this.groundMat);
    this.ground.rotation.x = -Math.PI / 2; // lie flat on y=0
    this.ground.receiveShadow = false;
    // Centre the plane under the kill box (mid-depth), in Three coords.
    this.ground.position.set(0, 0, -RENDER.camera.lookAtZ);
    scene.add(this.ground);
  }

  /** Per-theme key sun + cool sky/ground fill. */
  private buildLights(scene: THREE.Scene): void {
    const t = this.theme();
    this.sun = new THREE.DirectionalLight(t.sunColor, t.sunIntensity);
    // A LOW raking angle from the front-left so the ground picks up a clear
    // bright-near → shadow-far gradient and silhouettes throw long shadows toward
    // the camera. Low Y = grazing light = contrast.
    this.sun.position.set(-58, 34, 70);
    this.sun.target.position.set(0, 0, -RENDER.camera.lookAtZ);
    scene.add(this.sun);
    scene.add(this.sun.target);

    // Hemispheric fill: cool sky from above, warm bounce from below — the split
    // of cool shadow vs warm key without flattening the frame.
    this.hemi = new THREE.HemisphereLight(t.ambientColor, t.groundFill, t.ambientIntensity);
    scene.add(this.hemi);
  }

  /** Low-poly prop pool: wrecked-car-ish boxes, rubble, barrels. */
  private buildProps(scene: THREE.Scene): void {
    this.propGroup = new THREE.Group();
    scene.add(this.propGroup);
    this.propVariants = this.buildPropVariants();

    this.propPool = new ObjectPool<Prop>(
      () => {
        // Pick a variant by round-robin on current free count for variety.
        const idx = this.liveProps.length % this.propVariants.length;
        const template = this.propVariants[idx];
        const mesh = (template ?? this.propVariants[0])!.clone(true);
        const prop: Prop = { mesh, worldX: 0, worldZ: 0, active: false };
        return prop;
      },
      (p) => {
        p.active = false;
        this.propGroup.remove(p.mesh);
      },
    );

    // Pre-place propCount props spread evenly through the depth band so the
    // world is full of motion from frame one (no empty warm-up).
    const span = RENDER.world.propSpawnZ - RENDER.world.propDespawnZ;
    for (let i = 0; i < RENDER.world.propCount; i++) {
      const p = this.propPool.acquire();
      // Stagger depth across the whole band; lateral across the ground width.
      const z = RENDER.world.propDespawnZ + ((i + 0.5) / RENDER.world.propCount) * span;
      this.spawnProp(p, z);
    }
  }

  /** Load + share one PBR map set (color + normal + roughness [+ metalness]) the
   *  same way as the ground: SRGB on the colour map, RepeatWrapping, anisotropy 4,
   *  per-prop tile repeat. Returns the maps as a struct; textures are tracked for
   *  disposal. `metalnessName` is optional (concrete has no metalness map). */
  private loadPropMaps(
    prefix: string,
    repeat: number,
    metalnessName?: string,
  ): {
    map: THREE.Texture;
    normalMap: THREE.Texture;
    roughnessMap: THREE.Texture;
    metalnessMap: THREE.Texture | null;
  } {
    const loader = new THREE.TextureLoader();
    const base = import.meta.env.BASE_URL + 'assets/textures/';
    const map = loader.load(base + prefix + '_color.jpg');
    map.colorSpace = THREE.SRGBColorSpace; // colour is SRGB
    const normalMap = loader.load(base + prefix + '_normal.jpg');
    const roughnessMap = loader.load(base + prefix + '_roughness.jpg');
    const metalnessMap = metalnessName ? loader.load(base + metalnessName) : null;
    // normal/roughness/metalness stay LINEAR (default colorSpace = NoColorSpace).
    const all = [map, normalMap, roughnessMap];
    if (metalnessMap) all.push(metalnessMap);
    for (const tex of all) {
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(repeat, repeat);
      tex.anisotropy = 4;
      this.propTextures.push(tex);
    }
    return { map, normalMap, roughnessMap, metalnessMap };
  }

  /** Per-prop tint jitter so cloned variants never read identical. Returns the
   *  base tint nudged ±jitter on each channel (render-only rng stream — prop
   *  cosmetics must never advance the SIM stream). */
  private jitterTint(base: number, jitter: number): THREE.Color {
    const c = new THREE.Color(base);
    const j = (): number => 1 + (this.ctx.renderRng.next() * 2 - 1) * jitter;
    c.r = Math.min(1, Math.max(0, c.r * j()));
    c.g = Math.min(1, Math.max(0, c.g * j()));
    c.b = Math.min(1, Math.max(0, c.b * j()));
    return c;
  }

  /** A rusted-metal PBR material instance (shared maps, jittered rust tint). */
  private makeMetalMat(maps: ReturnType<ColorWorld['loadPropMaps']>): THREE.MeshStandardMaterial {
    const P = RENDER.props.metal;
    const m = new THREE.MeshStandardMaterial({
      map: maps.map,
      normalMap: maps.normalMap,
      roughnessMap: maps.roughnessMap,
      metalnessMap: maps.metalnessMap,
      color: this.jitterTint(P.tint, RENDER.props.tintJitter),
      roughness: P.roughness,
      metalness: P.metalness,
      normalScale: new THREE.Vector2(P.normalScale, P.normalScale),
      envMapIntensity: RENDER.light.envIntensity,
      fog: true,
    });
    this.propMaterials.push(m);
    return m;
  }

  /** A weathered-concrete PBR material instance (shared maps, jittered tint). */
  private makeConcreteMat(
    maps: ReturnType<ColorWorld['loadPropMaps']>,
  ): THREE.MeshStandardMaterial {
    const P = RENDER.props.concrete;
    const m = new THREE.MeshStandardMaterial({
      map: maps.map,
      normalMap: maps.normalMap,
      roughnessMap: maps.roughnessMap,
      color: this.jitterTint(P.tint, RENDER.props.tintJitter),
      roughness: P.roughness,
      metalness: P.metalness,
      normalScale: new THREE.Vector2(P.normalScale, P.normalScale),
      envMapIntensity: RENDER.light.envIntensity,
      fog: true,
    });
    this.propMaterials.push(m);
    return m;
  }

  /** Build a small set of reusable prop template meshes (cloned per instance).
   *  Two SHARED PBR map sets are loaded once (rusted metal + weathered concrete);
   *  cars + barrels wear the rust, barriers + rubble wear the concrete. Each
   *  variant gets its own tint-jittered material instance off the shared maps so
   *  the recycled props read war-torn and varied rather than identical boxes. */
  private buildPropVariants(): THREE.Object3D[] {
    const variants: THREE.Object3D[] = [];

    // Two shared PBR map sets (rusted metal reuses the gun's Metal032; concrete
    // is Concrete016). Repeat tuned per material so detail isn't stretched.
    const metalMaps = this.loadPropMaps(
      'gunmetal',
      RENDER.props.metal.textureRepeat,
      'gunmetal_metalness.jpg',
    );
    const concreteMaps = this.loadPropMaps('concrete', RENDER.props.concrete.textureRepeat);

    // --- Variant A: wrecked car (body + cabin + WHEELS + a dark window band) —
    //     RUST. The wheels + glass band break the old "two stacked boxes" read. ---
    {
      const carBody = new THREE.BoxGeometry(2.0, 0.8, 4.2);
      const carCabin = new THREE.BoxGeometry(1.7, 0.7, 2.0);
      const wheelGeo = new THREE.CylinderGeometry(0.38, 0.38, 0.3, 12);
      const windowGeo = new THREE.BoxGeometry(1.74, 0.34, 1.4);
      this.propGeometries.push(carBody, carCabin, wheelGeo, windowGeo);
      // A near-black rough material shared by the tyres + the dark glass band.
      const darkMat = new THREE.MeshStandardMaterial({
        color: 0x14161a,
        roughness: 0.85,
        metalness: 0.2,
        fog: true,
      });
      this.propMaterials.push(darkMat);
      const g = new THREE.Group();
      const body = new THREE.Mesh(carBody, this.makeMetalMat(metalMaps));
      body.position.y = 0.4;
      const cabin = new THREE.Mesh(carCabin, this.makeMetalMat(metalMaps));
      cabin.position.set(0, 1.0, -0.2);
      g.add(body, cabin);
      // Four tyres at the corners (axle along X), grounded under the body.
      const wheelPos: ReadonlyArray<readonly [number, number]> = [
        [0.95, 1.4],
        [-0.95, 1.4],
        [0.95, -1.4],
        [-0.95, -1.4],
      ];
      for (const [wx, wz] of wheelPos) {
        const w = new THREE.Mesh(wheelGeo, darkMat);
        w.rotation.z = Math.PI / 2; // cylinder axis Y → X (disc faces sideways)
        w.position.set(wx, 0.38, wz);
        g.add(w);
      }
      // A dark glass band wrapping the cabin (slightly proud to avoid z-fight).
      const glass = new THREE.Mesh(windowGeo, darkMat);
      glass.position.set(0, 1.05, -0.2);
      g.add(glass);
      g.rotation.y = 0.4;
      variants.push(g);
    }

    // --- Variant B: barrel cluster (three cylinders) — RUST ---
    {
      const barrel = new THREE.CylinderGeometry(0.35, 0.35, 1.0, 12);
      this.propGeometries.push(barrel);
      const g = new THREE.Group();
      const offsets: ReadonlyArray<readonly [number, number]> = [
        [0, 0],
        [0.7, 0.3],
        [0.35, -0.6],
      ];
      // One jittered rust material per barrel so the cluster isn't a clone-stamp.
      for (let i = 0; i < offsets.length; i++) {
        const o = offsets[i]!;
        const b = new THREE.Mesh(barrel, this.makeMetalMat(metalMaps));
        b.position.set(o[0], 0.5, o[1]);
        g.add(b);
      }
      variants.push(g);
    }

    // --- Variant C: rubble pile (a few scattered low boxes) — CONCRETE ---
    {
      const chunk = new THREE.BoxGeometry(0.9, 0.5, 0.9);
      this.propGeometries.push(chunk);
      const g = new THREE.Group();
      const chunks: ReadonlyArray<readonly [number, number, number, number]> = [
        [0, 0.25, 0, 0.2],
        [0.8, 0.2, 0.5, 0.9],
        [-0.6, 0.3, 0.4, -0.5],
        [0.3, 0.18, -0.7, 1.4],
      ];
      for (let i = 0; i < chunks.length; i++) {
        const c = chunks[i]!;
        const m = new THREE.Mesh(chunk, this.makeConcreteMat(concreteMaps));
        m.position.set(c[0], c[1], c[2]);
        m.rotation.y = c[3];
        m.scale.setScalar(0.7 + (i % 3) * 0.25);
        g.add(m);
      }
      variants.push(g);
    }

    // --- Variant D: concrete barrier (a long low slab) — CONCRETE ---
    {
      const slab = new THREE.BoxGeometry(3.4, 1.1, 0.7);
      this.propGeometries.push(slab);
      const g = new THREE.Group();
      const s = new THREE.Mesh(slab, this.makeConcreteMat(concreteMaps));
      s.position.y = 0.55;
      g.add(s);
      g.rotation.y = 0.15;
      variants.push(g);
    }

    return variants;
  }

  /** Position a (re)acquired prop at sim-depth `z` with a random lateral offset. */
  private spawnProp(p: Prop, z: number): void {
    // Lateral spread across most of the ground width, biased away from the
    // centre kill-lane so props frame the action rather than block it.
    // Render-only rng stream: prop placement recycles at render cadence, so the
    // SIM stream must never be drained here (determinism seam).
    const half = RENDER.world.groundSizeM * 0.42;
    let x = (this.ctx.renderRng.next() * 2 - 1) * half;
    // Nudge a touch out of the dead-centre firing lane.
    if (Math.abs(x) < 6) x += x >= 0 ? 6 : -6;
    p.worldX = x;
    p.worldZ = z;
    p.active = true;
    const t = worldToThree({ x, y: 0, z });
    p.mesh.position.set(t.x, t.y, t.z);
    p.mesh.rotation.y = this.ctx.renderRng.next() * Math.PI * 2;
    this.propGroup.add(p.mesh);
    this.liveProps.push(p);
  }

  /** The SPAWN-HAZE fog bank: layered translucent gradient quads straddling the
   *  enemy spawn band (RENDER.spawnHaze), tinted to the theme's fog colour.
   *  Fresh enemies materialise BEHIND these curtains and walk out of the murk
   *  (paired with GlbEnemyView's reveal fade) — no more open-field pop-in. */
  private buildSpawnHaze(): void {
    const H = RENDER.spawnHaze;
    // One shared gradient texture: opaque at the bottom-centre, dissolving
    // toward the top and the lateral edges (so the quads read as a fog BANK,
    // not hard-edged billboards).
    const c = document.createElement('canvas');
    c.width = 128;
    c.height = 64;
    const g2d = c.getContext('2d')!;
    const img = g2d.createImageData(c.width, c.height);
    for (let y = 0; y < c.height; y++) {
      // Vertical: dense at the ground, gone by the top.
      const vy = 1 - y / (c.height - 1);
      const vert = Math.min(1, vy * 1.6);
      for (let x = 0; x < c.width; x++) {
        // Horizontal: soft cosine falloff toward both lateral edges.
        const nx = (x / (c.width - 1)) * 2 - 1;
        const horiz = 0.5 + 0.5 * Math.cos(nx * Math.PI * 0.5);
        const i = (y * c.width + x) * 4;
        img.data[i] = 255;
        img.data[i + 1] = 255;
        img.data[i + 2] = 255;
        img.data[i + 3] = Math.round(255 * vert * horiz);
      }
    }
    g2d.putImageData(img, 0, 0);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    this.hazeTexture = tex;

    this.hazeGeo = new THREE.PlaneGeometry(H.widthM, H.heightM);
    const tint = new THREE.Color(this.theme().fogColor);
    for (let i = 0; i < H.curtainZ.length; i++) {
      const z = H.curtainZ[i] ?? 26;
      const mat = new THREE.MeshBasicMaterial({
        map: tex,
        color: tint,
        transparent: true,
        opacity: H.curtainOpacity[i] ?? 0.4,
        depthWrite: false,
        fog: false, // the curtain IS the fog — don't fog the fog
      });
      this.hazeMaterials.push(mat);
      const quad = new THREE.Mesh(this.hazeGeo, mat);
      const t = worldToThree({ x: 0, y: 0, z });
      quad.position.set(t.x, H.heightM / 2, t.z);
      quad.renderOrder = 2; // after opaque world; enemies behind it dissolve into it
      this.group.add(quad);
      this.hazeMeshes.push(quad);
    }
  }

  /** Pooled FOG-BURST puffs: a soft radial sprite that swells + fades where a
   *  unit walks clear of the spawn bank (RENDER.spawnHaze.burst) — the arrival
   *  reads as the fog itself parting, not a model fading in. Built once;
   *  spawnFogBurst() re-uses the pool round-robin. */
  private buildFogBursts(): void {
    const B = RENDER.spawnHaze.burst;
    // One shared soft radial-gradient texture.
    const s = 128;
    const c = document.createElement('canvas');
    c.width = c.height = s;
    const g = c.getContext('2d');
    if (g) {
      const grad = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
      grad.addColorStop(0, 'rgba(255,255,255,0.9)');
      grad.addColorStop(0.55, 'rgba(255,255,255,0.4)');
      grad.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = grad;
      g.fillRect(0, 0, s, s);
    }
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    this.fogBurstTexture = tex;

    const tint = new THREE.Color(this.theme().fogColor);
    for (let i = 0; i < B.poolSize; i++) {
      const mat = new THREE.SpriteMaterial({
        map: tex,
        color: tint,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        fog: false,
      });
      const sprite = new THREE.Sprite(mat);
      sprite.visible = false;
      sprite.renderOrder = 3; // over the haze curtains
      this.group.add(sprite);
      this.fogBursts.push({ sprite, ageMs: 0, active: false });
    }
  }

  /** Fire one emergence puff at a Three-space point (round-robin pool). */
  private spawnFogBurst(tx: number, tz: number): void {
    const B = RENDER.spawnHaze.burst;
    const slot = this.fogBursts[this.fogBurstIdx];
    this.fogBurstIdx = (this.fogBurstIdx + 1) % Math.max(1, this.fogBursts.length);
    if (!slot) return;
    const rng = this.ctx.renderRng;
    slot.sprite.position.set(tx + (rng.next() - 0.5) * 0.8, 1.1 + rng.next() * 0.5, tz);
    slot.sprite.scale.setScalar(B.sizeM * 0.7);
    slot.ageMs = 0;
    slot.active = true;
    slot.sprite.visible = true;
  }

  /** Advance live puffs: swell + fade (fast in, slow out), drift gently up. */
  private updateFogBursts(dt: number): void {
    const B = RENDER.spawnHaze.burst;
    const dtSec = dt / 1000;
    for (const fb of this.fogBursts) {
      if (!fb.active) continue;
      fb.ageMs += dt;
      const t = fb.ageMs / B.lifeMs;
      if (t >= 1) {
        fb.active = false;
        fb.sprite.visible = false;
        (fb.sprite.material as THREE.SpriteMaterial).opacity = 0;
        continue;
      }
      const fade = t < 0.2 ? t / 0.2 : 1 - (t - 0.2) / 0.8;
      (fb.sprite.material as THREE.SpriteMaterial).opacity = B.opacity * fade;
      fb.sprite.scale.setScalar(B.sizeM * (0.7 + 1.1 * t));
      fb.sprite.position.y += 0.45 * dtSec;
    }
  }

  /** Best-effort HDRI environment for subtle reflections/IBL. Never blocks. */
  private loadHdri(scene: THREE.Scene): void {
    try {
      const v = this.ctx.view as unknown as { renderer?: THREE.WebGLRenderer };
      const renderer = v.renderer;
      if (!renderer) return;
      this.pmrem = new THREE.PMREMGenerator(renderer);
      this.pmrem.compileEquirectangularShader();
      const url = import.meta.env.BASE_URL + 'assets/hdri/dusk_sky_1k.hdr';
      new RGBELoader().load(
        url,
        (hdr) => {
          if (this.disposed || !this.pmrem) {
            hdr.dispose();
            return;
          }
          const rt = this.pmrem.fromEquirectangular(hdr);
          this.envMap = rt.texture;
          scene.environment = this.envMap;
          hdr.dispose();
          this.pmrem.dispose();
          this.pmrem = null;
        },
        undefined,
        () => {
          // Load failed — lights alone carry the scene. Clean up the generator.
          this.pmrem?.dispose();
          this.pmrem = null;
        },
      );
    } catch {
      this.pmrem?.dispose();
      this.pmrem = null;
    }
  }

  private readonly onSpawned = (p: { id: number; def: string }): void => {
    const view = this.pool.pop() ?? new GlbEnemyView();
    view.assign(p.def as Parameters<GlbEnemyView['assign']>[0]);
    view.setEnemyId(p.id);
    view.revealClearZ = this.revealClearZM; // difficulty-tuned emergence plane
    this.group.add(view.group);
    this.views.set(p.id, view);
  };

  private readonly onKilled = (p: { id: number }): void => {
    // Detach from the live set, play the death animation, and let it linger a
    // beat (topple/collapse) before pooling — far more visceral than a pop-out.
    const view = this.views.get(p.id);
    if (!view) return;
    this.views.delete(p.id);
    this.emerged.delete(p.id);
    view.startDeath();
    this.dyingViews.push({ view, leftMs: 850 });
  };

  /** Bright warm hit flash on hit (driven by FiringSystem via GameLoop). */
  flash(id: number, ms: number): void {
    this.views.get(id)?.flash(ms);
  }

  /**
   * Raycast from the camera through an NDC point against all live enemy rigs.
   * Returns the nearest hit's enemy id, body zone, and world impact point.
   */
  raycast(ndcX: number, ndcY: number, camera: THREE.Camera): EnemyHit | null {
    if (this.views.size === 0) return null;
    this.ndc.set(ndcX, ndcY);
    this.raycaster.setFromCamera(this.ndc, camera);
    // Gather all hit meshes (rig parts) and intersect; nearest along the ray wins.
    const targets: THREE.Object3D[] = [];
    for (const v of this.views.values()) targets.push(...v.hitMeshes);
    const hits = this.raycaster.intersectObjects(targets, false);
    const h = hits[0];
    if (!h) return null;
    const id = h.object.userData.enemyId as number | undefined;
    if (id === undefined) return null;
    const zone = (h.object.userData.zone as EnemyHit['zone']) ?? 'body';
    return { id, zone, world: { x: h.point.x, y: h.point.y, z: -h.point.z } };
  }

  private retire(id: number): void {
    const view = this.views.get(id);
    if (!view) return;
    this.views.delete(id);
    this.emerged.delete(id);
    this.group.remove(view.group);
    view.hide();
    this.pool.push(view);
  }

  update(dt: number): void {
    const dtSec = dt / 1000;
    // The scroll MIRRORS the sim's worldDriftMps, so it must FREEZE with the
    // sim during hitstop — render keeps ticking (shake/VFX), but sliding the
    // terrain while enemy positions are frozen made the whole horde visibly
    // moonwalk backward on every kill (the audited "they move backwards" read).
    const scrollDist = this.ctx.simFrozen ? 0 : RENDER.flight.scrollMps * dtSec;

    // 1) Scroll the ground: advance the texture V offset so the asphalt slides
    //    toward the near edge (forward motion). RepeatWrapping wraps it.
    this.scrollV =
      (this.scrollV + (scrollDist / RENDER.world.groundSizeM) * RENDER.world.groundTextureRepeat) %
      1;
    const map = this.groundMat.map;
    if (map) map.offset.y = this.scrollV;
    const nrm = this.groundMat.normalMap;
    if (nrm) nrm.offset.y = this.scrollV;
    const rgh = this.groundMat.roughnessMap;
    if (rgh) rgh.offset.y = this.scrollV;

    // 2) Slide props toward the near edge; recycle past the despawn line.
    for (let i = this.liveProps.length - 1; i >= 0; i--) {
      const p = this.liveProps[i]!;
      p.worldZ -= scrollDist;
      if (p.worldZ <= RENDER.world.propDespawnZ) {
        // Recycle: swap-remove from liveProps, release, re-acquire at far edge.
        const last = this.liveProps[this.liveProps.length - 1]!;
        this.liveProps[i] = last;
        this.liveProps.pop();
        this.propPool.release(p);
        const fresh = this.propPool.acquire();
        this.spawnProp(fresh, RENDER.world.propSpawnZ);
      } else {
        // worldToThree is a pure z-flip; set z directly (no alloc).
        p.mesh.position.z = -p.worldZ;
      }
    }

    // 2b) Advance the distant skyline parallax + drifting smoke columns (they
    //     slide + recycle with the SAME world scroll as the props).
    this.backdrop.update(dt, scrollDist);

    // 2c) Slide the overpass barriers with the same scroll (modular wrap).
    if (this.rails) {
      this.railOffset += scrollDist;
      this.layoutRails();
    }

    // 3) Sync the COLOR horde (IDENTICAL to WorldView3D's enemy loop).
    const live = this.liveIds;
    live.clear();
    const burstAtZ = this.revealClearZM + RENDER.spawnHaze.burst.triggerAheadM;
    for (const e of this.ctx.enemies) {
      live.add(e.id);
      const view = this.views.get(e.id);
      if (!view) continue;
      const def = getEnemy(e.def);
      const t = worldToThree(e.pos);
      view.update(t.x, t.y, t.z, def.speed * (e.speedMult ?? 1), dt, e.pos.z);

      // Emergence beat: the unit just walked clear of the spawn bank — part
      // the fog around it ONCE (the puff masks the tail of the reveal fade).
      if (!this.emerged.has(e.id) && e.pos.z <= burstAtZ) {
        this.emerged.add(e.id);
        this.spawnFogBurst(t.x, t.z);
      }

      // Screen-space cache (sx/sy at the rig's mid-height, scale = px-per-metre)
      // so the FiringSystem rect hit-test + any 2D markers stay valid.
      const proj = this.ctx.view.worldToScreen({ x: e.pos.x, y: def.heightM * 0.5, z: e.pos.z });
      e.sx = proj.sx;
      e.sy = proj.sy;
      e.scale = proj.scale;
    }
    // Retire any rigs whose enemy left the list without a kill event (e.g. strike).
    for (const id of this.views.keys()) {
      if (!live.has(id)) this.retire(id);
    }

    // 4) Advance dying rigs (death animation) + pool them when the beat ends.
    //    A corpse is ON the ground, so it RIDES the world scroll (Three z is
    //    the world-z flip: world z shrinking by scrollDist = Three z growing)
    //    — a body that held its position while the asphalt slid under it read
    //    as the dead moonwalking away from the bird.
    for (let i = this.dyingViews.length - 1; i >= 0; i--) {
      const d = this.dyingViews[i]!;
      d.view.group.position.z += scrollDist;
      // Keep the death pose ticking in place (its mixer was started; just step it).
      d.view.update(d.view.group.position.x, d.view.group.position.y, d.view.group.position.z, 0, dt, 2);
      d.leftMs -= dt;
      if (d.leftMs <= 0) {
        this.group.remove(d.view.group);
        d.view.hide();
        this.pool.push(d.view);
        const last = this.dyingViews[this.dyingViews.length - 1]!;
        this.dyingViews[i] = last;
        this.dyingViews.pop();
      }
    }

    // 5) Advance the emergence fog-burst puffs (render-side juice; dt-based).
    this.updateFogBursts(dt);
  }

  // Reused per-frame set (no allocation in update()).
  private readonly liveIds = new Set<number>();
  // Enemies playing their death animation before returning to the pool.
  private readonly dyingViews: Array<{ view: GlbEnemyView; leftMs: number }> = [];

  destroy(): void {
    this.disposed = true;
    this.ctx?.bus.off('enemy:spawned', this.onSpawned);
    this.ctx?.bus.off('enemy:killed', this.onKilled);
    this.ctx?.bus.off('hit:landed', this.onHit);

    // Enemy rigs.
    for (const v of this.views.values()) v.dispose();
    for (const d of this.dyingViews) d.view.dispose();
    for (const v of this.pool) v.dispose();
    this.views.clear();
    this.dyingViews.length = 0;
    this.pool.length = 0;

    // Ground.
    this.ground?.geometry.dispose();
    this.groundMat?.dispose();
    for (const t of this.groundTextures) t.dispose();
    this.groundTextures.length = 0;

    // Props.
    for (const g of this.propGeometries) g.dispose();
    this.propGeometries.length = 0;
    for (const m of this.propMaterials) m.dispose();
    this.propMaterials.length = 0;
    for (const t of this.propTextures) t.dispose();
    this.propTextures.length = 0;
    this.liveProps.length = 0;

    // Distant skyline + smoke columns.
    this.backdrop?.dispose();

    // Spawn-haze curtains.
    for (const m of this.hazeMeshes) this.group?.remove(m);
    this.hazeMeshes.length = 0;
    for (const m of this.hazeMaterials) m.dispose();
    this.hazeMaterials.length = 0;
    this.hazeGeo?.dispose();
    this.hazeGeo = null;

    // Emergence fog-burst pool.
    for (const fb of this.fogBursts) {
      this.group?.remove(fb.sprite);
      fb.sprite.material.dispose();
    }
    this.fogBursts.length = 0;
    this.fogBurstTexture?.dispose();
    this.fogBurstTexture = null;
    this.emerged.clear();
    this.hazeTexture?.dispose();
    this.hazeTexture = null;

    // Overpass barriers.
    if (this.rails) {
      this.group?.remove(this.rails);
      this.rails.dispose();
      this.rails = null;
    }
    this.railGeo?.dispose();
    this.railMat?.dispose();
    this.railGeo = null;
    this.railMat = null;

    // Lights.
    const scene = this.tryScene();
    if (scene) {
      if (this.sun) {
        scene.remove(this.sun, this.sun.target);
      }
      if (this.hemi) scene.remove(this.hemi);
      if (this.group) scene.remove(this.group);
      if (this.propGroup) scene.remove(this.propGroup);
      if (this.ground) scene.remove(this.ground);
      if (this.envMap && scene.environment === this.envMap) scene.environment = null;
      if (this.skyTexture && scene.background === this.skyTexture) scene.background = null;
    }
    this.sun?.dispose();
    this.hemi?.dispose();

    // HDRI.
    this.envMap?.dispose();
    this.envMap = null;
    this.pmrem?.dispose();
    this.pmrem = null;

    // Sky gradient.
    this.skyTexture?.dispose();
    this.skyTexture = null;
  }

  /** Scene lookup that swallows errors (destroy may run after the view is gone). */
  private tryScene(): THREE.Scene | null {
    try {
      return this.scene();
    } catch {
      return null;
    }
  }
}
